/**
 * AdSchedulePage.vue
 *
 * 廣告排程投放系統頁面
 *
 * 主要功能：
 * - 提供多個「版位」(slot) 的廣告排程設定，透過頁籤切換不同版位。
 * - 每個版位可設定：
 *   - 檔期名稱、排程期間、每日播放時間、單次播放時長
 *   - 投放目標群組（顯示群組內機台資訊）
 *   - 廣告內容（支援圖片廣告或互動問答）
 * - 圖片廣告支援圖片上傳（含格式、大小、尺寸檢查與預覽）
 * - 互動問答支援從題庫選擇題目並預覽內容
 * - 表單驗證與重置、發布狀態提示
 *
 * 主要資料結構：
 * - slots: 版位列表
 * - machineGroups: 機台群組資料（含群組內門市資訊）
 * - questionBank: 問答題庫
 *
 * 主要方法：
 * - handleTabChange: 切換版位時重置表單
 * - handleAdTypeChange: 廣告類型切換時重置相關欄位
 * - handleGroupChange: 選擇群組時顯示群組資訊
 * - handleQuestionChange: 選擇問答時顯示預覽
 * - beforeImageUpload: 上傳圖片前檢查格式、大小、尺寸
 * - handleImageChange: 處理圖片上傳與預覽
 * - disabledDate: 禁用過去日期
 * - handleSubmit: 表單驗證與提交（模擬 API 呼叫）
 * - handleReset/resetForm: 重置表單與狀態
 *
 * UI 框架：
 * - 使用 Ant Design Vue 元件（a-tabs, a-form, a-input, a-upload, a-select, a-radio-group 等）
 * - Tailwind CSS 輔助樣式
 *
 * 注意事項：
 * - 圖片上傳僅支援 JPG/PNG，最大 2MB，建議尺寸 1920x1080 px
 * - 問答題目需從題庫選擇，並顯示正確答案
 * - 所有欄位皆有表單驗證
 */
/**
 * AdSchedulePage.vue
 *
 * 廣告排程投放系統頁面
 *
 * 主要功能：
 * - 提供多個「版位」(slot) 的廣告排程設定，透過頁籤切換不同版位。
 * - 每個版位可設定：
 *   - 檔期名稱、排程期間、每日播放時間、單次播放時長
 *   - 投放目標群組（顯示群組內機台資訊）
 *   - 廣告內容（支援圖片廣告或互動問答）
 * - 圖片廣告支援圖片上傳（含格式、大小、尺寸檢查與預覽）
 * - 互動問答支援從題庫選擇題目並預覽內容
 * - 表單驗證與重置、發布狀態提示
 *
 * 主要資料結構：
 * - slots: 版位列表
 * - machineGroups: 機台群組資料（含群組內門市資訊）
 * - questionBank: 問答題庫
 *
 * 主要方法：
 * - handleTabChange: 切換版位時重置表單
 * - handleAdTypeChange: 廣告類型切換時重置相關欄位
 * - handleGroupChange: 選擇群組時顯示群組資訊
 * - handleQuestionChange: 選擇問答時顯示預覽
 * - beforeImageUpload: 上傳圖片前檢查格式、大小、尺寸
 * - handleImageChange: 處理圖片上傳與預覽
 * - disabledDate: 禁用過去日期
 * - handleSubmit: 表單驗證與提交（模擬 API 呼叫）
 * - handleReset/resetForm: 重置表單與狀態
 *
 * UI 框架：
 * - 使用 Ant Design Vue 元件（a-tabs, a-form, a-input, a-upload, a-select, a-radio-group 等）
 * - Tailwind CSS 輔助樣式
 *
 * 注意事項：
 * - 圖片上傳僅支援 JPG/PNG，最大 2MB，建議尺寸 1920x1080 px
 * - 問答題目需從題庫選擇，並顯示正確答案
 * - 所有欄位皆有表單驗證
 */
<template>
  <div class="min-h-screen bg-gray-50 p-4">
    <div class="max-w-7xl mx-auto">
      <!-- 頁面標題 -->
      <div class="mb-2">
        <h1 class="text-3xl font-bold text-gray-900 mb-2">廣告排程投放系統</h1>
        <p class="text-gray-600">設定各版位的廣告排程與投放內容</p>
      </div>

      <!-- 版位頁籤 -->
      <a-tabs v-model="activeSlot" type="card" class="bg-white rounded-lg shadow-sm" @change="handleTabChange">
        <a-tab-pane v-for="slot in slots" :key="slot.key" :tab="`${slot.name}`">
          <!-- 設定表單 -->
          <div class="p-2">
            <a-form :form="form" layout="vertical" @submit="handleSubmit" class="max-w-7xl">
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <!-- 左側：基本設定 -->
                <div class="space-y-6">
                  <div class="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h3 class="text-lg font-semibold text-blue-800 mb-4 flex items-center">
                      <a-icon type="setting" class="mr-2" />
                      基本設定
                    </h3>

                    <!-- 檔期名稱 -->
                    <a-form-item label="檔期名稱" class="mb-4">
                      <a-input
                        v-decorator="[
                          'campaignName',
                          {
                            rules: [
                              { required: true, message: '請輸入檔期名稱' },
                              { max: 50, message: '檔期名稱不能超過50個字元' },
                            ],
                          },
                        ]"
                        placeholder="請輸入檔期名稱，例如：春節活動廣告"
                        size="large"
                      />
                    </a-form-item>
                    <!-- 排程日期 -->
                    <a-form-item label="排程期間" class="mb-4">
                      <a-range-picker
                        v-decorator="[
                          'dateRange',
                          {
                            rules: [
                              { required: true, message: '請選擇排程期間' },
                              {
                                validator: validateDateRange,
                              },
                            ],
                            initialValue: [moment(), moment().add(1, 'day')],
                          },
                        ]"
                        :show-time="{ format: 'HH:mm:ss' }"
                        format="YYYY-MM-DD HH:mm:ss"
                        :placeholder="['開始日期時間', '結束日期時間']"
                        size="large"
                        style="width: 100%"
                        :disabled-date="disabledDate"
                      />
                    </a-form-item>

                    <!-- 每日播放時間 -->
                    <a-form-item label="每日播放時間" class="mb-4">
                      <div class="flex space-x-2">
                        <a-form-item style="width: 50%; margin-bottom: 0;">
                          <a-time-picker
                            v-decorator="[
                              'timeRangeStart',
                              {
                                initialValue: moment('00:00:00', 'HH:mm:ss'),
                                rules: [{ required: true, message: '請選擇開始時間' }],
                              },
                            ]"
                            format="HH:mm:ss"
                            size="large"
                            placeholder="開始時間"
                            style="width: 100%"
                          />
                        </a-form-item>
                        <a-form-item style="width: 50%; margin-bottom: 0;">
                          <a-time-picker
                            v-decorator="[
                              'timeRangeEnd',
                              {
                                initialValue: moment('23:59:59', 'HH:mm:ss'),
                                rules: [{ required: true, message: '請選擇結束時間' }],
                              },
                            ]"
                            format="HH:mm:ss"
                            size="large"
                            placeholder="結束時間"
                            style="width: 100%"
                          />
                        </a-form-item>
                      </div>
                    </a-form-item>

                    <!-- 播放時長（僅圖片廣告需要） -->
                    <a-form-item v-if="currentAdType !== 'interactive_qa'" label="單次播放時長 (秒)" class="mb-0">
                      <a-input-number
                        v-decorator="[
                          'duration',
                          {
                            initialValue: 10,
                            rules: [
                              { required: currentAdType !== 'interactive_qa', message: '請輸入播放時長' },
                              { type: 'number', min: 1, max: MAX_DURATION, message: `播放時長必須介於1-${MAX_DURATION}秒之間` },
                            ],
                          },
                        ]"
                        :min="1"
                        :max="MAX_DURATION"
                        size="large"
                        style="width: 100%"
                      />
                    </a-form-item>

                    <!-- 優先級別 -->
                    <a-form-item label="優先級別" class="mb-4">
                      <div class="text-xs text-gray-500 mb-1">優先級越高，同時段衝突時優先顯示（5最高，1最低）</div>
                      <a-select
                        v-decorator="[
                          'priority',
                          {
                            initialValue: 3,
                            rules: [{ required: true, message: '請設定優先級別' }],
                          },
                        ]"
                        size="large"
                        style="width: 100%"
                      >
                        <a-select-option v-for="option in priorityOptions" :key="option.value" :value="option.value">
                          {{ option.label }}
                        </a-select-option>
                      </a-select>
                    </a-form-item>
                  </div>

                  <!-- 群組選擇 -->
                  <div class="bg-green-50 p-4 rounded-lg border border-green-200">
                    <h3 class="text-lg font-semibold text-green-800 mb-4 flex items-center">
                      <a-icon type="team" class="mr-2" />
                      目標群組
                    </h3>

                    <a-form-item label="選擇機台群組" class="mb-0">
                      <a-select
                        v-decorator="[
                          'groupId',
                          {
                            rules: [{ required: true, message: '請選擇機台群組' }],
                          },
                        ]"
                        placeholder="請選擇要投放的機台群組"
                        size="large"
                        show-search
                        option-filter-prop="children"
                        @change="handleGroupChange"
                      >
                        <a-select-option v-for="group in machineGroups" :key="group.id" :value="group.id">
                          {{ group.name }} ({{ group.stores_data.length }}台機器)
                        </a-select-option>
                      </a-select>
                    </a-form-item>

                    <!-- 顯示選中群組的描述 -->
                    <div v-if="selectedGroupDescription" class="mt-4 p-3 bg-white rounded border">
                      <p class="text-sm text-gray-600">{{ selectedGroupDescription }}</p>
                    </div>
                  </div>
                </div>

                <!-- 右側：廣告內容 -->
                <div class="space-y-6">
                  <div class="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <h3 class="text-lg font-semibold text-purple-800 mb-4 flex items-center">
                      <a-icon type="picture" class="mr-2" />
                      廣告內容設定
                    </h3>

                    <!-- 廣告類型選擇 -->
                    <a-form-item label="廣告類型" class="">
                      <a-radio-group
                        v-decorator="[
                          'adType',
                          {
                            initialValue: 'image',
                            rules: [{ required: true, message: '請選擇廣告類型' }],
                          },
                        ]"
                        @change="handleAdTypeChange"
                        size="large"
                      >
                        <a-radio value="image">
                          <a-icon type="file-image" class="mr-1" />
                          圖片廣告
                        </a-radio>
                        <a-radio v-if="activeSlot === 1" value="interactive_qa">
                          <a-icon type="question-circle" class="mr-1" />
                          互動問答
                        </a-radio>
                      </a-radio-group>
                    </a-form-item>

                    <!-- 圖片上傳區域 -->
                    <div v-if="currentAdType === 'image'">
                      <a-form-item label="廣告圖片" class="">
                        <div class="flex flex-wrap gap-2">
                          <!-- 可拖曳的圖片列表 -->
                          <draggable
                            v-model="draggableFileList"
                            class="flex flex-wrap gap-2"
                            animation="200"
                          >
                            <div
                              v-for="(file, index) in draggableFileList"
                              :key="file.uid || index"
                              class="relative group w-[104px] h-[104px] border border-gray-200 rounded p-1 flex items-center justify-center overflow-hidden hover:border-blue-500 transition-colors bg-white"
                            >
                              <img :src="file.url || file.thumbUrl" class="max-w-full max-h-full object-contain" />

                              <!-- 遮罩與操作按鈕 -->
                              <div class="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2">
                                <a-icon type="eye" class="text-white cursor-pointer hover:text-blue-400 text-lg" @click="handlePreview(file)" />
                                <a-icon type="delete" class="text-white cursor-pointer hover:text-red-400 text-lg" @click="removeFile(file)" />
                              </div>
                            </div>
                          </draggable>

                          <!-- 上傳按鈕 -->
                          <a-upload
                            v-if="draggableFileList.length < 10"
                            v-decorator="[
                              'imageFile',
                              {
                                rules: [{ required: true, message: '請上傳至少一張廣告圖片' }],
                              },
                            ]"
                            name="file"
                            list-type="picture-card"
                            :show-upload-list="false"
                            :file-list="draggableFileList"
                            :multiple="true"
                            :before-upload="beforeImageUpload"
                            :customRequest="customUploadRequest"
                            @change="handleImageChange"
                            class="custom-uploader"
                          >
                            <div class="flex flex-col items-center justify-center text-gray-500">
                              <a-icon type="plus" />
                              <div class="ant-upload-text">上傳圖片</div>
                            </div>
                          </a-upload>
                        </div>

                        <!-- 圖片預覽模態窗 -->
                        <a-modal :visible="previewVisible" :footer="null" @cancel="previewVisible = false">
                          <img alt="預覽圖片" style="width: 100%" :src="previewImage" />
                        </a-modal>

                        <!-- 上傳數量提示 -->
                        <div class="mt-2 text-sm text-gray-500">
                          已上傳 {{ draggableFileList.length }}/10 張圖片 (可拖曳排序)
                        </div>
                      </a-form-item>

                      <!-- 圖片規格說明 -->
                      <div class="text-sm text-gray-500 bg-gray-100 p-3 rounded">
                        <a-icon type="info-circle" class="mr-1" />
                        圖片規格要求：
                        <ul class="mt-1 ml-4 list-disc">
                          <li>檔案大小：不超過 {{ MAX_FILE_SIZE_MB }}MB</li>
                          <li>檔案格式：JPG, PNG</li>
                          <li>建議尺寸：最小 {{ MIN_IMAGE_WIDTH }}x{{ MIN_IMAGE_HEIGHT }} 像素</li>
                        </ul>
                      </div>
                    </div>

                    <!-- 問答選擇區域 -->
                    <div v-else-if="currentAdType === 'interactive_qa'">
                      <a-form-item label="選擇問答題目" class="mb-2">
                        <a-select
                          v-decorator="[
                            'questionId',
                            {
                              rules: [{ required: true, message: '請選擇問答題目' }],
                            },
                          ]"
                          placeholder="請選擇要使用的問答題目"
                          size="large"
                          show-search
                          option-filter-prop="children"
                          @change="handleQuestionChange"
                        >
                          <a-select-option v-for="question in questionBank" :key="question.id" :value="question.id">
                            {{ question.title }}
                          </a-select-option>
                        </a-select>
                      </a-form-item>

                      <!-- 問答預覽 -->
                      <div v-if="selectedQuestion" class="bg-white p-4 rounded border">
                        <h4 class="font-medium mb-2">{{ selectedQuestion.title }}</h4>
                        <p class="text-gray-700 mb-3">{{ selectedQuestion.question }}</p>
                        <div class="space-y-2">
                          <div
                            v-for="(option, index) in selectedQuestion.options.options"
                            :key="index"
                            :class="['p-2 rounded border', selectedQuestion.correct_answer === option ? 'bg-green-100 border-green-300' : 'bg-gray-50 border-gray-200']"
                          >
                            {{ index + 1 }}. {{ option }}
                            <a-icon v-if="selectedQuestion.correct_answer === option" type="check-circle" class="text-green-600 ml-2" />
                          </div>
                        </div>
                      </div>

                      <!-- 問答背景圖片上傳區域：三個不同狀態的背景圖 -->
                      <div v-if="currentAdType === 'interactive_qa'" class="space-y-4 mt-4">
                        <!-- 1. 問答前背景圖上傳 -->
                        <a-form-item label="問答前背景圖" :required="true">
                          <div class="text-sm text-gray-500 mb-2">
                            <a-icon type="info-circle" class="mr-1" />
                            此圖片將作為問答題目顯示時的背景
                          </div>
                          <a-upload
                            v-decorator="[
                              'backgroundBeforeImage',
                              {
                                rules: [{ required: true, message: '請上傳問答前背景圖' }],
                              },
                            ]"
                            name="file"
                            list-type="picture-card"
                            :file-list="backgroundBeforeList"
                            :before-upload="(file) => beforeQuestionImageUpload(file, 'before')"
                            :customRequest="customUploadRequest"
                            @change="(info) => handleQuestionImageChange(info, 'before')"
                            @preview="handlePreview"
                          >
                            <div v-if="backgroundBeforeList.length < 1">
                              <a-icon type="plus" />
                              <div class="ant-upload-text">上傳問答前背景圖</div>
                            </div>
                          </a-upload>
                        </a-form-item>

                        <!-- 2. 答對背景圖上傳 -->
                        <a-form-item label="答對背景圖" :required="true">
                          <div class="text-sm text-gray-500 mb-2">
                            <a-icon type="info-circle" class="mr-1" />
                            此圖片將作為答對時顯示的背景
                          </div>
                          <a-upload
                            v-decorator="[
                              'backgroundCorrectImage',
                              {
                                rules: [{ required: true, message: '請上傳答對背景圖' }],
                              },
                            ]"
                            name="file"
                            list-type="picture-card"
                            :file-list="backgroundCorrectList"
                            :before-upload="(file) => beforeQuestionImageUpload(file, 'correct')"
                            :customRequest="customUploadRequest"
                            @change="(info) => handleQuestionImageChange(info, 'correct')"
                            @preview="handlePreview"
                          >
                            <div v-if="backgroundCorrectList.length < 1">
                              <a-icon type="plus" />
                              <div class="ant-upload-text">上傳答對背景圖</div>
                            </div>
                          </a-upload>
                        </a-form-item>

                        <!-- 3. 答錯背景圖上傳 -->
                        <a-form-item label="答錯背景圖" :required="true">
                          <div class="text-sm text-gray-500 mb-2">
                            <a-icon type="info-circle" class="mr-1" />
                            此圖片將作為答錯時顯示的背景
                          </div>
                          <a-upload
                            v-decorator="[
                              'backgroundWrongImage',
                              {
                                rules: [{ required: true, message: '請上傳答錯背景圖' }],
                              },
                            ]"
                            name="file"
                            list-type="picture-card"
                            :file-list="backgroundWrongList"
                            :before-upload="(file) => beforeQuestionImageUpload(file, 'wrong')"
                            :customRequest="customUploadRequest"
                            @change="(info) => handleQuestionImageChange(info, 'wrong')"
                            @preview="handlePreview"
                          >
                            <div v-if="backgroundWrongList.length < 1">
                              <a-icon type="plus" />
                              <div class="ant-upload-text">上傳答錯背景圖</div>
                            </div>
                          </a-upload>
                        </a-form-item>

                        <!-- 圖片規格說明 -->
                        <div class="text-sm text-gray-500 bg-gray-100 p-3 rounded mt-2">
                          <a-icon type="info-circle" class="mr-1" />
                          問答背景圖片規格：
                          <ul class="mt-1 ml-4 list-disc">
                            <li>需上傳三張背景圖：問答前、答對、答錯</li>
                            <li>檔案大小：不超過 {{ MAX_FILE_SIZE_MB }}MB</li>
                            <li>檔案格式：JPG, PNG, GIF</li>
                            <li>建議尺寸：1024x1280 像素</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 操作按鈕 -->
                  <div class="flex justify-end space-x-4 pt-6">
                    <a-button size="large" @click="handleReset">
                      <a-icon type="redo" />
                      重置
                    </a-button>
                    <a-button type="primary" size="large" :loading="publishing" @click="handleSubmit" :disabled="publishing">
                      <a-icon type="rocket" />
                      發布廣告
                    </a-button>
                  </div>
                </div>
              </div>
            </a-form>
          </div>
        </a-tab-pane>
      </a-tabs>
    </div>

  </div>
</template>

<script>
import moment from 'moment'
import { debounce } from 'lodash'
import { Upload } from 'ant-design-vue'
import draggable from 'vuedraggable'
// 定義常數
const MAX_FILE_SIZE_MB = 4
const MIN_IMAGE_WIDTH = 100
const MIN_IMAGE_HEIGHT = 100
const MAX_DURATION = 60
const MAX_CAMPAIGN_DAYS = 365 // 廣告活動最長天數

export default {
  name: 'AdSchedulePage',
  components: {
    draggable
  },
  data () {
    return {
      // API 路徑配置
      baseURL: this.GLOBAL.baseURL,
      basePort: this.GLOBAL.basePort,
      // 定義常數 (供模板使用)
      MAX_FILE_SIZE_MB,
      MIN_IMAGE_WIDTH,
      MIN_IMAGE_HEIGHT,
      MAX_DURATION,
      moment, // 替換為 moment，使模板能夠訪問

      activeSlot: 1, // 改為數字型別
      slots: [
        { key: 1, name: '版位一', code: 'SLOT_1' }, // 改為數字型別
        { key: 2, name: '版位二', code: 'SLOT_2' },
        { key: 3, name: '版位三', code: 'SLOT_3' }
      ],
      form: this.$form.createForm(this),
      publishing: false,
      // slot 狀態獨立
      slotStates: {
        1: { currentAdType: 'image', fileList: [], backgroundBeforeList: [], backgroundCorrectList: [], backgroundWrongList: [], selectedQuestion: null, selectedGroupDescription: null },
        2: { currentAdType: 'image', fileList: [], backgroundBeforeList: [], backgroundCorrectList: [], backgroundWrongList: [], selectedQuestion: null, selectedGroupDescription: null },
        3: { currentAdType: 'image', fileList: [], backgroundBeforeList: [], backgroundCorrectList: [], backgroundWrongList: [], selectedQuestion: null, selectedGroupDescription: null }
      },
      // 新增圖片預覽相關狀態
      previewVisible: false,
      previewImage: '',
      machineGroups: [],
      // 新增表單提交進度跟踪
      submitStep: '',
      submitProgress: 0,
      // 新增錯誤處理
      formErrors: {},
      defaultPriority: 3, // 新增優先級
      priorityOptions: [
        { value: 5, label: '非常高 (5)' },
        { value: 4, label: '高 (4)' },
        { value: 3, label: '中 (3)' },
        { value: 2, label: '低 (2)' },
        { value: 1, label: '非常低 (1)' }
      ],
      questionBank: [] // 問答題庫
    }
  },

  computed: {
    /**
     * 返回當前激活的時段對象
     * 通過activeSlot與slots數組中的key進行匹配
     */
    currentSlot () {
      return this.slots.find((slot) => slot.key === this.activeSlot)
    },

    /**
     * 返回當前激活時段的狀態
     * 從slotStates對象中獲取
     */
    currentSlotState () {
      return this.slotStates[this.activeSlot]
    },

    /**
     * 獲取當前廣告類型
     * 如果未設置則默認為'image'類型
     */
    currentAdType () {
      return this.currentSlotState?.currentAdType || 'image'
    },

    // 修改 imageUrl 和 imageFile 的計算屬性
    fileList () {
      return this.currentSlotState?.fileList || []
    },
    // 可拖曳的檔案列表 (支援 v-model)
    draggableFileList: {
      get () {
        return this.currentSlotState?.fileList || []
      },
      set (value) {
        if (this.currentSlotState) {
          this.currentSlotState.fileList = value
        }
      }
    },

    // 背景圖列表 - 問答前
    backgroundBeforeList () {
      return this.currentSlotState?.backgroundBeforeList || []
    },

    // 背景圖列表 - 答對
    backgroundCorrectList () {
      return this.currentSlotState?.backgroundCorrectList || []
    },

    // 背景圖列表 - 答錯
    backgroundWrongList () {
      return this.currentSlotState?.backgroundWrongList || []
    },
    /**
     * 獲取當前圖片URL
     * 從當前時段狀態中提取
     */
    imageUrl () {
      return this.currentSlotState?.imageUrl || ''
    },

    /**
     * 獲取當前圖片文件對象
     * 從當前時段狀態中提取
     */
    imageFile () {
      return this.currentSlotState?.imageFile || null
    },

    /**
     * 獲取當前選中的問題
     * 從當前時段狀態中提取
     */
    selectedQuestion () {
      return this.currentSlotState?.selectedQuestion || null
    },

    /**
     * 獲取當前選中的組描述
     * 從當前時段狀態中提取
     */
    selectedGroupDescription () {
      return this.currentSlotState?.selectedGroupDescription || null
    },

    /**
     * 驗證表單是否有效
     * 必須選擇一個組，且根據廣告類型必須有相應的內容:
     * - 圖片類型需要有imageFile
     * - 問題類型需要有selectedQuestion
     */
    formIsValid () {
      const hasSelectedGroup = !!this.selectedGroupDescription
      const adTypeValid = this.currentAdType === 'image' ? !!this.imageFile : !!this.selectedQuestion

      return hasSelectedGroup && adTypeValid
    }
  },
  created () {
    // 給提交函數加入防抖處理
    this.debouncedSubmit = debounce(this.submit, 500)
    // this.debouncedSubmit = debounce(async () => {
    //   if (this.publishing) return

    //   try {
    //     // 設置提交狀態
    //     this.publishing = true
    //     this.submitProgress = 10
    //     this.submitStep = '驗證表單數據...'

    //     await this.submit()
    //   } catch (error) {
    //     console.error('提交錯誤:', error)
    //     this.$message.error(error.message || '提交失敗')
    //   } finally {
    //     this.publishing = false
    //     this.submitProgress = 0
    //     this.submitStep = ''
    //   }
    // }, 500)
  },
  mounted () {
    // 初始化時獲取機台群組和問答題庫
    this.fetchMachineGroups()
    this.fetchQuestionBank()

    // // 初始化表單驗證
    // this.form.validateFields()
  },
  methods: {
    // 獲取機台群組列表
    fetchMachineGroups () {
      this.axios
        .get(`${this.baseURL}${this.basePort}/ads/groups`)
        .then((res) => {
          if (res.data.message === 'success') {
            this.machineGroups = res.data.data
          } else {
            this.$message.error('獲取機台群組失敗：' + res.data.message)
          }
        })
        .catch((error) => {
          console.error('獲取機台群組出錯:', error)
          this.$message.error('獲取機台群組失敗，請稍後重試')
        })
        .finally(() => {
          this.loadingGroups = false
        })
    },
    // 獲取問答題庫列表
    fetchQuestionBank () {
      this.axios
        .get(`${this.baseURL}${this.basePort}/questionBank/list`)
        .then((res) => {
          if (res.data.message === 'success') {
            this.questionBank = res.data.data
          } else {
            this.$message.error('獲取問答題庫失敗：' + res.data.message)
          }
        })
        .catch((error) => {
          console.error('獲取問答題庫出錯:', error)
          this.$message.error('獲取問答題庫失敗，請稍後重試')
        })
        .finally(() => {
          this.loadingQuestions = false
        })
    },
    // 處理頁籤切換
    handleTabChange (key) {
      // 轉換為數字型態以保持一致性
      this.activeSlot = parseInt(key, 10)
      console.log('切換到版位:', key)
      console.log(this.activeSlot)

      this.form.resetFields()
      this.resetForm()
    },
    // 新增日期範圍驗證器
    validateDateRange (rule, value, callback) {
      if (!value || value.length !== 2) {
        callback()
        return
      }
      const startDateTime = value[0]
      const endDateTime = value[1]

      // 使用 diff 時考慮到時、分、秒
      const diffMilliseconds = endDateTime.diff(startDateTime)
      const diffDays = endDateTime.diff(startDateTime, 'day', true) // 使用 true 參數獲取小數點

      if (diffMilliseconds <= 0) {
        callback(new Error('結束時間必須晚於開始時間'))
        return
      }

      if (diffDays > MAX_CAMPAIGN_DAYS) {
        callback(new Error(`廣告活動期間不能超過 ${MAX_CAMPAIGN_DAYS} 天`))
        return
      }

      callback()
    },
    // 處理廣告類型切換
    handleAdTypeChange (e) {
      const slotState = this.currentSlotState
      slotState.currentAdType = e.target.value
      if (slotState.currentAdType === 'image') {
        // 切換到圖片廣告時
        slotState.selectedQuestion = null
        slotState.questionImageList = [] // 清空舊的問答圖片列表(已棄用)
        slotState.backgroundBeforeList = [] // 清空問答前背景圖
        slotState.backgroundCorrectList = [] // 清空答對背景圖
        slotState.backgroundWrongList = [] // 清空答錯背景圖
        // 重置問答相關表單字段
        this.form.setFieldsValue({
          questionId: undefined,
          backgroundBeforeImage: undefined,
          backgroundCorrectImage: undefined,
          backgroundWrongImage: undefined
        })
      } else {
        // 切換到問答廣告時
        slotState.imageUrl = ''
        slotState.imageFile = null
        slotState.fileList = [] // 確保清空圖片列表

        // 重置圖片相關表單字段
        this.form.setFieldsValue({
          imageFile: undefined
        })
      }
    },
    // 處理群組選擇
    handleGroupChange (groupId) {
      const selectedGroup = this.machineGroups.find((group) => group.id === groupId)
      if (selectedGroup) {
        this.currentSlotState.selectedGroupDescription = selectedGroup.description
      } else {
        this.currentSlotState.selectedGroupDescription = null
      }
    },
    // 處理問答選擇
    handleQuestionChange (questionId) {
      this.currentSlotState.selectedQuestion = this.questionBank.find((q) => q.id === questionId)
    }, // 圖片上傳前檢查
    // 修改圖片上傳前檢查
    beforeImageUpload (file) {
      // 檢查檔名是否包含空格
      if (file.name.includes(' ')) {
        this.$message.error('檔案名稱不能包含空格，請重新命名後再上傳')
        return false
      }

      // 檢查當前上傳數量
      if (this.fileList.length >= 10) {
        this.$message.error('最多只能上傳10張圖片')
        return Upload.LIST_IGNORE
      }

      // 現有檢查代碼保持不變
      try {
        // 檢查檔案類型
        const fileType = file.type
        // const fileName = file.name.toLowerCase()

        // 寬鬆的檢查，只要是圖片類型就接受
        const isImage = fileType.startsWith('image/')

        // 如果需要嚴格檢查 JPG/PNG/GIF
        const isJpg = fileType === 'image/jpeg' || fileType === 'image/jpg'
        const isPng = fileType === 'image/png'
        const isGif = fileType === 'image/gif'

        if (!isImage) {
          this.$message.error('請上傳圖片檔案！')
          return false
        }

        if (!isJpg && !isPng && !isGif) {
          this.$message.error('只能上傳 JPG 、 PNG 、 GIF 格式的圖片！')
          return false
        }

        // 檢查檔案大小
        const isLt2M = file.size / 1024 / 1024 < MAX_FILE_SIZE_MB
        if (!isLt2M) {
          this.$message.error(`圖片大小不能超過 ${MAX_FILE_SIZE_MB}MB！`)
          return false
        }

        // 檢查圖片尺寸 - 改為 true 以避免顯示問題
        return new Promise((resolve) => {
          const img = new Image()
          img.onload = () => {
            console.log('圖片尺寸:', img.width, img.height)
            const isValidSize = img.width >= MIN_IMAGE_WIDTH && img.height >= MIN_IMAGE_HEIGHT
            if (!isValidSize) {
              this.$message.warning(`圖片尺寸建議至少 ${MIN_IMAGE_WIDTH}x${MIN_IMAGE_HEIGHT} 像素，但仍可使用`)
              // 即使尺寸不符合也允許上傳
              resolve(true)
            } else {
              resolve(true)
            }
          }
          img.onerror = () => {
            this.$message.error('圖片載入失敗，請確認檔案格式正確')
            resolve(false)
          }
          img.src = URL.createObjectURL(file)
        })
      } catch (error) {
        console.error('檢查圖片錯誤:', error)
        this.$message.error('檢查圖片時發生錯誤：' + error.message)
        return false
      }
    },
    // 新增方法：驗證問答圖片數量
    validateQuestionImages (rule, value, callback) {
      if (this.currentAdType !== 'interactive_qa') {
        callback()
        return
      }

      if (!this.questionImageList || this.questionImageList.length !== 2) {
        callback(new Error('必須上傳兩張問答展示圖片'))
        return
      }

      callback()
    },
    // 新增方法：問答圖片上傳前檢查
    beforeQuestionImageUpload (file, type) {
      // 檢查當前上傳數量
      let imageList
      switch (type) {
        case 'before':
          imageList = this.backgroundBeforeList
          break
        case 'correct':
          imageList = this.backgroundCorrectList
          break
        case 'wrong':
          imageList = this.backgroundWrongList
          break
        default:
          imageList = []
      }

      if (imageList.length >= 1) {
        const typeText = type === 'before' ? '問答前背景圖' : type === 'correct' ? '答對背景圖' : '答錯背景圖'
        this.$message.error(`${typeText}只能上傳1張`)
        return Upload.LIST_IGNORE
      }

      // 檢查檔名是否包含空格
      if (file.name.includes(' ')) {
        this.$message.error('檔案名稱不能包含空格，請重新命名後再上傳')
        return false
      }

      // 檢查檔案類型
      const fileType = file.type
      // 檢查是否為圖片
      const isImage = fileType.startsWith('image/')
      // 檢查是否為支援的格式
      const isJpg = fileType === 'image/jpeg' || fileType === 'image/jpg'
      const isPng = fileType === 'image/png'
      const isGif = fileType === 'image/gif'

      if (!isImage) {
        this.$message.error('請上傳圖片檔案！')
        return false
      }

      if (!isJpg && !isPng && !isGif) {
        this.$message.error('只能上傳 JPG、PNG、GIF 格式的圖片！')
        return false
      }

      // 檢查檔案大小
      const isLt2M = file.size / 1024 / 1024 < MAX_FILE_SIZE_MB
      if (!isLt2M) {
        this.$message.error(`圖片大小不能超過 ${MAX_FILE_SIZE_MB}MB！`)
        return false
      }

      // 檢查圖片尺寸
      return new Promise((resolve) => {
        const img = new Image()
        img.onload = () => {
          const isValidSize = img.width >= MIN_IMAGE_WIDTH && img.height >= MIN_IMAGE_HEIGHT
          if (!isValidSize) {
            this.$message.warning(`圖片尺寸建議至少 ${MIN_IMAGE_WIDTH}x${MIN_IMAGE_HEIGHT} 像素，但仍可使用`)
          }
          resolve(true)
        }
        img.onerror = () => {
          this.$message.error('圖片載入失敗，請確認檔案格式正確')
          resolve(false)
        }
        img.src = URL.createObjectURL(file)
      })
    },

    // 新增方法：處理問答背景圖片變更
    handleQuestionImageChange ({ file, fileList }, type) {
      const slotState = this.currentSlotState

      // 限制只能上傳1張圖片
      if (fileList.length > 1) {
        const typeText = type === 'before' ? '問答前背景圖' : type === 'correct' ? '答對背景圖' : '答錯背景圖'
        this.$message.warning(`${typeText}只能上傳1張`)
        fileList = fileList.slice(0, 1)
      }

      // 更新對應類型的圖片列表
      switch (type) {
        case 'before':
          slotState.backgroundBeforeList = fileList
          break
        case 'correct':
          slotState.backgroundCorrectList = fileList
          break
        case 'wrong':
          slotState.backgroundWrongList = fileList
          break
      }

      // 處理預覽URL
      fileList.forEach((f) => {
        if (f.originFileObj && !f.url) {
          f.url = URL.createObjectURL(f.originFileObj)
        }
      })

      // 觸發表單驗證
      this.$nextTick(() => {
        const fieldName = type === 'before' ? 'backgroundBeforeImage'
          : type === 'correct' ? 'backgroundCorrectImage' : 'backgroundWrongImage'
        this.form.validateFields([fieldName], { force: true })
      })
    },

    // 修改圖片上傳處理 - 確保檔案完整保存
    handleImageChange ({ file, fileList }) {
      const slotState = this.currentSlotState

      if (file.status === 'removed') {
        slotState.fileList = fileList
        if (fileList.length === 0) {
          slotState.imageUrl = ''
        } else {
          slotState.imageUrl = fileList[0].url || URL.createObjectURL(fileList[0].originFileObj)
        }
      } else if (file.status === 'done' || file.status === 'uploading') {
        // 🆕 確保 originFileObj 被保留
        slotState.fileList = fileList.map((f) => {
          if (f.originFileObj && !f.url) {
            f.url = URL.createObjectURL(f.originFileObj)
          }
          // 🆕 確保 originFileObj 存在
          if (!f.originFileObj && f.file) {
            f.originFileObj = f.file
          }
          return f
        })

        if (file.originFileObj) {
          slotState.imageUrl = URL.createObjectURL(file.originFileObj)
        }
      }
    },

    // 新增圖片預覽處理
    handlePreview (file) {
      console.log('預覽圖片:', file)
      this.previewImage = file.url || file.thumbUrl
      this.previewVisible = true
    },

    removeFile (file) {
      const slotState = this.currentSlotState
      slotState.fileList = slotState.fileList.filter((f) => f.uid !== file.uid)
      // 如果刪除後還有圖片，更新預覽圖為第一張
      if (slotState.fileList.length > 0) {
        slotState.imageUrl = slotState.fileList[0].url
      } else {
        slotState.imageUrl = ''
      }
    },
    // 移除圖片 (現在不需要單獨的方法，由 handleImageChange 處理)

    // 修改重置表單
    resetForm () {
      const slotState = this.currentSlotState
      slotState.currentAdType = 'image'
      slotState.fileList = [] // 清空文件列表
      slotState.questionImageList = [] // 清空問答圖片列表
      slotState.selectedQuestion = null
      slotState.selectedGroupDescription = null
    },

    // 移除圖片
    removeImage () {
      const slotState = this.currentSlotState
      slotState.imageUrl = ''
      slotState.imageFile = null
      this.form.setFieldsValue({ imageFile: undefined })
    },

    // 禁用過去的日期
    disabledDate (current) {
      return current && current < moment().startOf('day')
    },

    // 處理表單提交 (增加錯誤處理)
    handleSubmit (e) {
      console.log('提交表單')
      if (e) {
        e.preventDefault() // 防止表單默認提交行為
      }

      // 使用防抖處理
      this.debouncedSubmit()
    },

    // 實際提交函數
    async submit () {
      console.log('實際提交函數被調用')
      if (this.publishing) {
        console.log('正在發布中，請稍後再試')
        return
      }

      this.form.validateFields(async (err, values) => {
        console.log('表單驗證結果:', err ? '驗證失敗' : '驗證成功')
        if (err) {
          console.error('表單驗證錯誤:', err)
          this.$message.error('請填寫完整表單資料')
          return
        }

        try {
          this.publishing = true
          const slotState = this.currentSlotState

          // 檢查廣告類型特定資料
          if (values.adType === 'image' && (!slotState.fileList || slotState.fileList.length === 0)) {
            this.$message.error('請上傳至少一張廣告圖片')
            this.publishing = false
            return
          } else if (values.adType === 'interactive_qa') {
            // 檢查問答題目
            if (!values.questionId) {
              this.$message.error('請選擇問答題目')
              this.publishing = false
              return
            }

            // 檢查三個背景圖
            if (!slotState.backgroundBeforeList || slotState.backgroundBeforeList.length === 0) {
              this.$message.error('請上傳問答前背景圖片')
              this.publishing = false
              return
            }

            if (!slotState.backgroundCorrectList || slotState.backgroundCorrectList.length === 0) {
              this.$message.error('請上傳答對背景圖片')
              this.publishing = false
              return
            }

            if (!slotState.backgroundWrongList || slotState.backgroundWrongList.length === 0) {
              this.$message.error('請上傳答錯背景圖片')
              this.publishing = false
              return
            }
          }

          // 🆕 創建 FormData 物件用於檔案上傳
          const formData = new FormData()

          // 添加基本資料
          formData.append('ad_type', values.adType)
          formData.append('campaign_name', values.campaignName)
          formData.append('slot_id', this.currentSlot.key)
          formData.append('group_id', values.groupId)
          formData.append('start_datetime', values.dateRange[0].toISOString())
          formData.append('end_datetime', values.dateRange[1].toISOString())
          formData.append('start_time', values.timeRangeStart.format('HH:mm:ss'))
          formData.append('end_time', values.timeRangeEnd.format('HH:mm:ss'))
          formData.append('priority', values.priority)
          // 若 duration 為 undefined (如問答模式)，則預設為 10
          const duration = values.duration !== undefined ? values.duration : 10
          formData.append('duration_seconds', duration)
          formData.append('display_order', values.displayOrder || 1)

          // 🆕 根據廣告類型添加特定資料和檔案
          if (values.adType === 'image') {
            // 添加圖片檔案
            slotState.fileList.forEach((file, index) => {
              const actualFile = file.originFileObj || file
              formData.append('images', actualFile)
              console.log(`添加圖片檔案 ${index + 1}:`, actualFile.name)
            })
          } else if (values.adType === 'interactive_qa') {
            // 添加問答 ID
            formData.append('question_id', values.questionId)

            // 添加問答背景圖片及其類型資訊
            const questionImageTypes = []

            // 添加問答前背景圖
            if (slotState.backgroundBeforeList.length > 0) {
              const beforeFile = slotState.backgroundBeforeList[0].originFileObj || slotState.backgroundBeforeList[0]
              formData.append('images', beforeFile)
              questionImageTypes.push('background_before')
              console.log('添加問答前背景圖檔案:', beforeFile.name)
            }

            // 添加答對背景圖
            if (slotState.backgroundCorrectList.length > 0) {
              const correctFile = slotState.backgroundCorrectList[0].originFileObj || slotState.backgroundCorrectList[0]
              formData.append('images', correctFile)
              questionImageTypes.push('background_correct')
              console.log('添加答對背景圖檔案:', correctFile.name)
            }

            // 添加答錯背景圖
            if (slotState.backgroundWrongList.length > 0) {
              const wrongFile = slotState.backgroundWrongList[0].originFileObj || slotState.backgroundWrongList[0]
              formData.append('images', wrongFile)
              questionImageTypes.push('background_wrong')
              console.log('添加答錯背景圖檔案:', wrongFile.name)
            }

            // 添加圖片類型對應
            questionImageTypes.forEach(type => {
              formData.append('question_image_types', type)
            })
          }

          console.log('準備提交 FormData 到後端...')

          // 檢查排程衝突
          const hasConflict = await this.checkScheduleConflict()
          console.log('是否有衝突：', hasConflict)

          if (hasConflict === false) {
            // 🆕 直接阻止新增並顯示錯誤訊息
            this.$modal.error({
              title: '排程衝突警告',
              content: '系統檢測到此時段已有相同優先級的廣告排程，無法新增重複排程。請調整時間範圍或優先級後再試。',
              okText: '我知道了'
            })
            this.publishing = false
            return
          }

          // 🆕 使用 FormData 提交，並設置正確的 Content-Type
          const response = await this.axios.post(
        `${this.baseURL}${this.basePort}/adCampaigns/create`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          // 可選：添加上傳進度追蹤
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            )
            console.log(`上傳進度: ${percentCompleted}%`)
            // 可以在這裡更新 UI 顯示上傳進度
          }
        }
          )

          if (response.data.message === 'success') {
            // 🆕 成功處理，包含 ZIP 處理結果
            const { data, processing_info: processingInfo } = response.data

            this.$message.success(`廣告排程設定成功！檔期「${values.campaignName}」已建立`)

            // 🆕 顯示檔案處理結果
            let descriptionContent = `版位：${this.currentSlot.name}
期間：${data.start_datetime} 至 ${data.end_datetime}
廣告ID：${data.campaign_id}`

            if (processingInfo) { // ✅ 正確：使用 camelCase
              descriptionContent += `
檔案上傳：${processingInfo.files_uploaded} 個檔案
ZIP 處理：${processingInfo.zip_status === 'completed' ? '成功' : processingInfo.zip_status === 'failed' ? '失敗' : '處理中'}`

              if (processingInfo.zip_size_mb) {
                descriptionContent += `
ZIP 大小：${processingInfo.zip_size_mb} MB`
              }
            }

            this.$notification.success({
              message: '廣告排程建立成功',
              description: descriptionContent,
              duration: 8
            })

            // 🆕 如果有 ZIP 下載連結，提供下載選項
            if (data.zip_download_url) {
              this.$notification.info({
                message: '檔案下載',
                description: `所有上傳的圖片已打包為 ZIP 檔案，點擊下載。${data.zip_download_expires || ''}`,
                duration: 15,
                btn: h => {
                  return h('a-button', {
                    props: {
                      type: 'primary',
                      size: 'small',
                      icon: 'download'
                    },
                    on: {
                      click: () => {
                        window.open(data.zip_download_url, '_blank')
                      }
                    }
                  }, '下載 ZIP')
                }
              })
            }

            // 重置表單
            this.handleReset()
          } else {
            throw new Error(response.data.errorMsg || '建立廣告排程失敗')
          }
        } catch (error) {
          console.error('提交廣告排程錯誤:', error)

          if (error.response) {
            console.error('API 回應狀態:', error.response.status)
            console.error('API 回應資料:', error.response.data)
          }

          const errorMsg = error.response?.data?.errorMsg || error.message || '未知錯誤'
          this.$message.error('提交失敗：' + errorMsg)

          if (errorMsg.includes('優先級')) {
            this.$notification.warning({
              message: '優先級衝突',
              description: '此版位在所選時間段已有相同優先級的廣告排程，請調整優先級或時間',
              duration: 10
            })
          }
        } finally {
          this.publishing = false
        }
      })
    },

    // 重置表單
    handleReset () {
      this.form.resetFields()
      this.resetForm()
    }, // 重置表單相關狀態

    // 自定義上傳請求處理
    customUploadRequest ({ file, onSuccess, onError }) {
      // 不再模擬，直接成功，讓檔案保留在 fileList 中供後續提交使用
      setTimeout(() => {
        try {
          // 確保檔案對象完整
          onSuccess(null, file)
        } catch (err) {
          console.error('檔案處理錯誤', err)
          onError(err)
        }
      }, 100)
    },
    // 驗證每日播放時間範圍
    validateTimeRange (rule, value, callback) {
      const start = this.form.getFieldValue('timeRangeStart')
      const end = this.form.getFieldValue('timeRangeEnd')

      if (start && end) {
        // 轉換為分鐘進行比較
        const startMinutes = start.hours() * 60 + start.minutes()
        const endMinutes = end.hours() * 60 + end.minutes()

        if (endMinutes <= startMinutes) {
          callback(new Error('結束時間必須晚於開始時間'))
          return
        }
      }

      callback()
    },
    // 檢查排程衝突
    async checkScheduleConflict () {
      try {
        const values = this.form.getFieldsValue()
        if (!values.dateRange || !values.groupId) return true

        const checkData = {
          slot_id: this.currentSlot.key,
          group_id: values.groupId,
          start_datetime: values.dateRange[0].format('YYYY-MM-DD HH:mm:ss'),
          end_datetime: values.dateRange[1].format('YYYY-MM-DD HH:mm:ss'),
          priority: values.priority || 3
        }

        const response = await this.axios.post(`${this.baseURL}${this.basePort}/adCampaigns/check-conflicts`, checkData)

        if (response.data.message === 'conflict') {
          this.showAdvancedNotification('warning', '潛在排程衝突', `檢測到此時段已有 ${response.data.data.count} 個相同優先級的廣告排程。建議調整優先級或時間段以避免衝突。`, 8)
          return false
        }

        return true
      } catch (error) {
        console.error('檢查排程衝突出錯:', error)
        return true // 發生錯誤時也允許提交，避免阻塞用戶操作
      }
    },
    // 顯示進階通知
    showAdvancedNotification (type, title, content, duration = 4.5) {
      const iconMap = {
        success: 'check-circle',
        info: 'info-circle',
        warning: 'warning',
        error: 'close-circle'
      }

      this.$notification[type]({
        message: title,
        description: content,
        duration,
        icon: (h) =>
          h('a-icon', {
            props: {
              type: iconMap[type],
              theme: 'filled'
            },
            style: {
              color: type === 'success' ? '#52c41a' : type === 'info' ? '#1890ff' : type === 'warning' ? '#faad14' : '#f5222d'
            }
          })
      })
    }
  }
}
</script>

<style scoped>
span :is(h1, h2, h3, h4):hover {
  color: blue;
  margin-bottom: 1em;
  background: #000;
}

.card:has(img) {
  border: 2px solid #61dafb;
  padding: 10px;
}
/* 自定義樣式 */
.avatar-uploader .ant-upload {
  width: 100%;
}

.ant-form-item-label {
  font-weight: 600;
}

/* 或者使用更高優先級的選擇器 */
html body div[class*="ant-modal-wrap"] > div[class*="ant-modal"] {
  top: 10px !important;
}

/* 頁籤樣式調整 - 使用新的 :deep() 語法替代 ::v-deep */
:deep(.ant-tabs-card > .ant-tabs-content) {
  margin-top: 0;
}

:deep(.ant-tabs-card > .ant-tabs-bar .ant-tabs-tab) {
  border-radius: 8px 8px 0 0;
  font-weight: 600;
}

:deep(.ant-tabs-card > .ant-tabs-bar .ant-tabs-tab-active) {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: #667eea;
  color: white;
}

/* 上傳區域樣式 */
:deep(.ant-upload.ant-upload-select-picture-card) {
  width: 100%;
  height: 128px;
}

/* 響應式調整 */
@media (max-width: 1024px) {
  .grid-cols-1.lg\\:grid-cols-2 {
    grid-template-columns: 1fr;
  }
}

/* 多圖上傳樣式 */
:deep(.ant-upload-list-picture-card .ant-upload-list-item) {
  width: 100px;
  height: 100px;
  margin: 0 8px 8px 0;
}

:deep(.ant-upload.ant-upload-select-picture-card) {
  width: 100px;
  height: 100px;
  margin: 0 8px 8px 0;
}

.ant-upload-text {
  margin-top: 8px;
  font-size: 12px;
}
</style>
